import {
  Mesh,
  ShaderMaterial,
  SphereGeometry,
  SRGBColorSpace,
  TextureLoader,
  Vector3,
  type Texture,
} from "three";

import earthVert from "./shaders/earth.vert.glsl?raw";
import earthFrag from "./shaders/earth.frag.glsl?raw";
import {
  EARTH_RADIUS,
  LAYERS,
  type LayerId,
  TEXTURE_DAY,
  TEXTURE_NIGHT,
  TEXTURE_WATER,
} from "./constants";

export interface EarthMesh {
  mesh: Mesh;
  material: ShaderMaterial;
  /** Land/water mask (water ≈ 1), shared with overlays that keep to land. */
  waterTexture: Texture;
  /** City lights, shared with the land mesh's night side. */
  nightTexture: Texture;
  setSunDirection(v: Vector3): void;
  setTheme(t: number): void;
  /** The land mesh draws the land: the globe shows sea only. */
  setLandMesh(on: boolean): void;
  /** Swap the day-side layer. Textures other than "satellite" are
   *  lazy-loaded on first use; subsequent swaps are instant. */
  setLayer(id: LayerId): Promise<void>;
  dispose(): void;
}

export async function createEarth(): Promise<EarthMesh> {
  const loader = new TextureLoader();
  loader.setCrossOrigin("anonymous");

  const [dayTex, nightTex, waterTex] = await Promise.all([
    loadTexture(loader, TEXTURE_DAY),
    loadTexture(loader, TEXTURE_NIGHT),
    loadTexture(loader, TEXTURE_WATER),
  ]);

  dayTex.colorSpace = SRGBColorSpace;
  nightTex.colorSpace = SRGBColorSpace;
  // Water mask is data, not colour — keep it linear
  dayTex.anisotropy = 8;
  nightTex.anisotropy = 8;
  waterTex.anisotropy = 4;

  // Cache for lazily-loaded non-default layer textures (physical, etc.)
  const layerCache = new Map<LayerId, Texture>();

  const material = new ShaderMaterial({
    vertexShader: earthVert,
    fragmentShader: earthFrag,
    uniforms: {
      uDayTex: { value: dayTex },
      uNightTex: { value: nightTex },
      uWaterTex: { value: waterTex },
      uSunDir: { value: new Vector3(1, 0, 0) },
      uTime: { value: 0 },
      uNightBoost: { value: 1.6 },
      // 0 = dark (observatory), 1 = light (milk-white page). Smoothly
      // interpolated so the toggle can animate if we want later.
      uTheme: { value: 0 },
      // 0 = live sun lighting, 1 = flat (layer is a map, not a sphere
      // with real-time terminator). Set by setLayer().
      uFlat: { value: 0 },
      // 1 = flat two-tone vector map from the water mask (Time zones).
      uVector: { value: 0 },
      uZoneFill: { value: 0 },
      uLandMesh: { value: 0 },
    },
  });

  // High tessellation — the silhouette lives in the rim region so any
  // faceting here would be visible against the atmosphere glow.
  const geometry = new SphereGeometry(EARTH_RADIUS, 128, 96);
  const mesh = new Mesh(geometry, material);
  mesh.renderOrder = 0;

  return {
    mesh,
    material,
    waterTexture: waterTex,
    nightTexture: nightTex,
    setSunDirection(v) {
      material.uniforms.uSunDir.value.copy(v).normalize();
    },
    setTheme(t) {
      material.uniforms.uTheme.value = t;
    },
    setLandMesh(on) {
      material.uniforms.uLandMesh.value = on ? 1 : 0;
    },
    async setLayer(id) {
      const layer = LAYERS[id];
      // Reuse the already-loaded satellite (dayTex) or night (nightTex)
      // textures when possible; otherwise lazy-load and cache.
      let tex;
      if (layer.dayUrl === TEXTURE_DAY) {
        tex = dayTex;
      } else if (layer.dayUrl === TEXTURE_NIGHT) {
        tex = nightTex;
      } else {
        const cached = layerCache.get(id);
        tex = cached ?? (await loadTexture(loader, layer.dayUrl));
        tex.colorSpace = SRGBColorSpace;
        tex.anisotropy = 8;
        layerCache.set(id, tex);
      }
      material.uniforms.uDayTex.value = tex;
      material.uniforms.uFlat.value = layer.flat;
      material.uniforms.uVector.value = layer.vector;
      material.uniforms.uZoneFill.value = layer.zones;
    },
    dispose() {
      geometry.dispose();
      material.dispose();
      dayTex.dispose();
      nightTex.dispose();
      waterTex.dispose();
      for (const t of layerCache.values()) t.dispose();
    },
  };
}

function loadTexture(loader: TextureLoader, url: string): Promise<Texture> {
  return new Promise((resolve, reject) => {
    loader.load(url, resolve, undefined, (err) => {
      const type = (err as { type?: string })?.type ?? "unknown";
      reject(new Error(`Texture load failed: ${url} (${type})`));
    });
  });
}
